import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { monthDay } from '@/lib/format';
import { card } from '../../shell/look';
import { FactLines, Truncated, caption } from './parts';

type Doc = Extract<WireArtifact, { kind: 'doc' }>;

/**
 * A generated document — a report — is its fields, so they are what is shown.
 * A document that only has extracted text keeps the paper sheet it reads like.
 * "Open original" and "Send to Telegram" are deferred (ADR 0001).
 */
export function DocArtifact({ artifact }: { artifact: Doc }): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className={`${card} px-4 py-3.5`}>
        <div className="flex items-baseline justify-between gap-3">
          <span className={caption}>DOCUMENT</span>
          <span className={caption}>{monthDay(artifact.at).toUpperCase()}</span>
        </div>
        <div className="mt-2 text-[15px] leading-snug font-semibold [overflow-wrap:anywhere]">
          {artifact.title}
        </div>
        {artifact.subtitle !== undefined && (
          <div className="text-mute mt-1 font-mono text-[11.5px] [overflow-wrap:anywhere]">
            {artifact.subtitle}
          </div>
        )}
        <FactLines facts={artifact.lines} />
      </div>
      {artifact.text !== '' && <Sheet text={artifact.text} />}
      {artifact.truncated && <Truncated what="part of the text" />}
    </div>
  );
}

/** Extracted text reads as a page, not as a field: white, serif, scrollable. */
function Sheet({ text }: { text: string }): JSX.Element {
  return (
    <div className="border-line max-h-[460px] overflow-y-auto rounded-xl border bg-white px-6 py-[22px] font-serif text-[11.5px] leading-[1.55] whitespace-pre-wrap text-[#222] [overflow-wrap:anywhere]">
      {text}
    </div>
  );
}
